if (!customElements.get('workshop-variant-picker')) {
  customElements.define(
    'workshop-variant-picker',
    class WorkshopVariantPicker extends HTMLElement {
      static MAX_QTY_PER_LAMP = 10;

      constructor() {
        super();

        this.variants = JSON.parse(this.querySelector('[data-workshop-variant-data]').textContent);
        this.strings = JSON.parse(this.querySelector('[data-workshop-strings]').textContent);
        this.lookup = new Map(this.variants.map((variant) => [`${variant.lamp}||${variant.date}||${variant.time}`, variant]));

        this.datetimeSelect = this.querySelector('[data-workshop-datetime]');
        this.scroller = this.querySelector('[data-workshop-scroller]');
        this.submitButton = this.querySelector('[data-workshop-submit]');
        this.submitButtonText = this.querySelector('[data-workshop-submit-text]');
        this.submitTotal = this.querySelector('[data-workshop-submit-total]');
        this.errorWrapper = this.querySelector('.workshop-picker__error');
        this.errorMessage = this.querySelector('.workshop-picker__error-message');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.mainPriceEl = document.getElementById(`price-${this.dataset.sectionId}`);
        this.baseCents = this.variants.length ? Math.min(...this.variants.map((variant) => variant.price)) : 0;
        this.previewLamp = null;

        this.datetimeSelect.addEventListener('change', this.onDatetimeChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));

        this.prevButton = this.querySelector('[data-workshop-scroll="prev"]');
        this.nextButton = this.querySelector('[data-workshop-scroll="next"]');
        this.prevButton?.addEventListener('click', () => this.scrollByCard(-1));
        this.nextButton?.addEventListener('click', () => this.scrollByCard(1));
        this.scroller?.addEventListener('scroll', this.updateScrollButtons.bind(this));

        this.updateScrollButtons();
        this.updateTotal();
      }

      get selectedOption() {
        return this.datetimeSelect.selectedOptions[0];
      }

      get selectedDate() {
        return this.selectedOption?.dataset.date || null;
      }

      get selectedTime() {
        return this.selectedOption?.dataset.time || null;
      }

      onDatetimeChange() {
        this.hideError();
        const date = this.selectedDate;
        const time = this.selectedTime;

        this.querySelectorAll('[data-lamp-card]').forEach((card) => {
          const key = `${card.dataset.lampValue}||${date}||${time}`;
          const variant = this.lookup.get(key);
          const available = Boolean(date && time && variant && variant.available);

          card.toggleAttribute('data-unavailable', !available);
          if (!available && card.dataset.qty !== '0') this.setQty(card, 0, false);
        });

        this.updateTotal();
      }

      onClick(event) {
        const addButton = event.target.closest('[data-lamp-add]');
        const plusButton = event.target.closest('[data-lamp-plus]');
        const minusButton = event.target.closest('[data-lamp-minus]');
        const removeButton = event.target.closest('[data-lamp-remove]');
        const imageButton = event.target.closest('[data-lamp-image]');
        const submitButton = event.target.closest('[data-workshop-submit]');

        if (addButton || plusButton) {
          const card = (addButton || plusButton).closest('[data-lamp-card]');
          if (card.hasAttribute('data-unavailable')) return;
          this.hideError();
          this.setPreview(card.dataset.lampValue);
          const qty = parseInt(card.dataset.qty || '0', 10);
          this.setQty(card, Math.min(qty + 1, WorkshopVariantPicker.MAX_QTY_PER_LAMP));
        } else if (minusButton) {
          const card = minusButton.closest('[data-lamp-card]');
          const qty = parseInt(card.dataset.qty || '0', 10);
          this.setQty(card, Math.max(qty - 1, 0));
        } else if (removeButton) {
          const card = removeButton.closest('[data-lamp-card]');
          this.setQty(card, 0);
        } else if (imageButton) {
          const card = imageButton.closest('[data-lamp-card]');
          if (card) this.setPreview(card.dataset.lampValue);
          this.showMainImage(imageButton.dataset.mediaId);
        } else if (submitButton) {
          this.onSubmit();
        }
      }

      showMainImage(mediaId) {
        if (!mediaId) return;
        document.querySelector('media-gallery')?.setActiveMedia(mediaId, false);
      }

      setQty(card, qty, recalcTotal = true) {
        card.dataset.qty = String(qty);
        card.querySelector('[data-lamp-qty]').textContent = qty;
        card.querySelector('[data-lamp-remove]').hidden = qty === 0;
        card.toggleAttribute('data-selected', qty > 0);
        if (recalcTotal) this.updateTotal();
      }

      currentTotalCents() {
        const date = this.selectedDate;
        const time = this.selectedTime;

        let cents = 0;
        this.querySelectorAll('[data-lamp-card]').forEach((card) => {
          const qty = parseInt(card.dataset.qty || '0', 10);
          if (qty === 0) return;
          const variant = this.lookup.get(`${card.dataset.lampValue}||${date}||${time}`);
          if (variant) cents += variant.price * qty;
        });

        return cents;
      }

      updateTotal() {
        const cents = this.currentTotalCents();
        if (this.submitTotal) {
          this.submitTotal.textContent = cents > 0 ? ` · ${this.formatMoney(cents)}` : '';
        }
        this.updateMainPrice();
      }

      setPreview(lampValue) {
        this.previewLamp = lampValue || null;
        this.querySelectorAll('[data-lamp-card]').forEach((card) => {
          card.toggleAttribute('data-previewed', card.dataset.lampValue === this.previewLamp);
        });
        this.updateMainPrice();
      }

      updateMainPrice() {
        if (!this.mainPriceEl) return;

        // Main price place = unit price of the last-clicked lamp only.
        // The combined total of all selected lamps lives on the add-to-cart button.
        let text;

        if (this.previewLamp != null) {
          const variant = this.variants.find((entry) => `${entry.lamp}` === `${this.previewLamp}`);
          text = this.formatMoney(variant ? variant.price : this.baseCents);
        } else {
          const base = this.formatMoney(this.baseCents);
          const template = this.strings.fromPriceTemplate;
          text = template ? template.replace('||PRICE||', base) : base;
        }

        this.mainPriceEl.innerHTML =
          '<div class="price price--large">' +
          '<div class="price__container">' +
          '<div class="price__regular">' +
          `<span class="price-item price-item--regular">${text}</span>` +
          '</div></div></div>';
      }

      formatMoney(cents) {
        const format = this.strings.moneyFormat || '${{amount}}';
        const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!placeholderMatch) return format;

        const amount = (cents / 100).toFixed(2);
        const [dollars, decimals] = amount.split('.');
        const withThousands = (value) => value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

        let value;
        switch (placeholderMatch[1]) {
          case 'amount_no_decimals':
            value = withThousands(Math.round(cents / 100).toString());
            break;
          case 'amount_with_comma_separator':
            value = `${withThousands(dollars)},${decimals}`;
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withThousands(Math.round(cents / 100).toString());
            break;
          case 'amount_with_space_separator':
            value = `${dollars.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')},${decimals}`;
            break;
          default:
            value = `${withThousands(dollars)}.${decimals}`;
        }

        return format.replace(placeholderMatch[0], value);
      }

      scrollByCard(direction) {
        const card = this.scroller.querySelector('[data-lamp-card]');
        if (!card) return;
        const amount = card.getBoundingClientRect().width + 12;
        this.scroller.scrollBy({ left: amount * direction, behavior: 'smooth' });
      }

      updateScrollButtons() {
        if (!this.prevButton || !this.nextButton || !this.scroller) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
        this.prevButton.disabled = scrollLeft <= 1;
        this.nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
      }

      showError(message) {
        this.errorMessage.textContent = message;
        this.errorWrapper.hidden = false;
      }

      hideError() {
        this.errorWrapper.hidden = true;
      }

      onSubmit() {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const date = this.selectedDate;
        const time = this.selectedTime;

        if (!date || !time) {
          this.showError(this.strings.selectDateTime);
          return;
        }

        const selectedCards = Array.from(this.querySelectorAll('[data-lamp-card]')).filter(
          (card) => parseInt(card.dataset.qty || '0', 10) > 0
        );

        if (selectedCards.length === 0) {
          this.showError(this.strings.selectLamp);
          return;
        }

        const updates = {};
        for (const card of selectedCards) {
          const key = `${card.dataset.lampValue}||${date}||${time}`;
          const variant = this.lookup.get(key);
          if (!variant || !variant.available) {
            this.showError(this.strings.soldOutForDatetime);
            return;
          }
          updates[variant.id] = parseInt(card.dataset.qty, 10);
        }

        this.hideError();
        this.addToCart(updates);
      }

      addToCart(updates) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const body = JSON.stringify({
          updates,
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : [],
          sections_url: window.location.pathname,
        });

        const updateUrl = routes.cart_update_url.endsWith('.js')
          ? routes.cart_update_url
          : `${routes.cart_update_url}.js`;

        fetch(updateUrl, { ...fetchConfig(), ...{ body } })
          .then(async (response) => {
            const text = await response.text();
            try {
              return JSON.parse(text);
            } catch (parseError) {
              console.error('Workshop picker: cart update did not return JSON', response.status, text.slice(0, 500));
              throw parseError;
            }
          })
          .then((parsedState) => {
            if (parsedState.errors) {
              const message =
                typeof parsedState.errors === 'string'
                  ? parsedState.errors
                  : Object.values(parsedState.errors).flat().join(' ') || window.cartStrings.error;
              this.showError(message);
              return;
            }

            // /cart/update.js silently caps quantities it can't fulfil instead of
            // returning an error, so verify each line actually landed before declaring success.
            const shortfall = Object.entries(updates).some(([variantId, qty]) => {
              const line = parsedState.items?.find((item) => `${item.variant_id}` === variantId);
              return !line || line.quantity < qty;
            });

            if (shortfall) {
              this.showError(this.strings.soldOutForDatetime);
              return;
            }

            // Shopify sometimes returns sections: null for this endpoint (seen consistently on
            // this store) even though the update itself succeeded. Refreshing the drawer/notification
            // UI is best-effort — a failure there must never be reported as a failed add-to-cart.
            if (this.cart && parsedState.sections) {
              try {
                this.cart.renderContents(parsedState);
                if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              } catch (renderError) {
                console.error('Workshop picker: cart UI refresh failed, items were still added', renderError);
              }
            } else {
              this.refreshCartCountFallback();
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'workshop-variant-picker', cartData: parsedState });
            this.resetSelections();
            this.flashAddedConfirmation();
          })
          .catch((e) => {
            console.error(e);
            this.showError(window.cartStrings.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      resetSelections() {
        this.previewLamp = null;
        this.querySelectorAll('[data-lamp-card]').forEach((card) => {
          card.removeAttribute('data-previewed');
          this.setQty(card, 0);
        });
        this.updateMainPrice();
      }

      flashAddedConfirmation() {
        if (!this.submitButtonText) return;
        const original = this.submitButtonText.textContent;
        this.submitButtonText.textContent = this.strings.addedConfirmation;
        setTimeout(() => {
          this.submitButtonText.textContent = original;
        }, 1800);
      }

      refreshCartCountFallback() {
        fetch(`${routes.cart_url}.json`)
          .then((response) => response.json())
          .then((cart) => {
            const bubble = document.querySelector('.cart-count-bubble');
            const count = bubble?.querySelector('span[aria-hidden]');
            if (count) count.textContent = cart.item_count;
          })
          .catch(() => {});
      }
    }
  );
}
